import createAdjDetailEntry from './createAdjDetailEntry'
import getAdjReason from './getAdjReason'
import isEmpty from './isEmpty'
import isReadonlyFatigueAdj from './isReadonlyFatigueAdj'

function appendUniqueReason(character, reasonKey, reasonText) {
  if (isEmpty(reasonText)) {
    return character
  }

  const existingReasons = isEmpty(character[reasonKey])
    ? []
    : character[reasonKey]
        .split('. ')
        .map((entry) => entry.trim())
        .filter((entry) => !isEmpty(entry))

  if (existingReasons.includes(reasonText)) {
    return character
  }

  if (existingReasons.length === 0) {
    character[reasonKey] = reasonText
  } else {
    character[reasonKey] += '. ' + reasonText
  }

  return character
}

const abilityKeys = ['ca', 'cr', 'sr', 'damage_mod_melee', 'damage_mod_ranged', 'move', 'move_fly']

function isReadonlySwitchAbilityAdj(character, adj) {
  if (!adj || adj.name !== 'cr') {
    return false
  }

  const reason = `${adj.adj_reason || ''}`.trim()

  if (reason === 'Shield Equipped') {
    return character.shield === true
  }

  if (reason === 'Second-Weapon Equipped') {
    return character.second_weapon === true
  }

  return false
}

function isPersistableSwitchAbilityAdj(adj) {
  if (!adj || adj.name !== 'cr' || adj.id == null) {
    return false
  }

  const reason = `${adj.adj_reason || ''}`.trim()

  return reason === 'Shield Equipped' || reason === 'Second-Weapon Equipped'
}

function isActiveSwitchAbilityAdj(character, adj) {
  if (!adj || adj.name !== 'cr') {
    return true
  }

  const reason = `${adj.adj_reason || ''}`.trim()

  if (reason === 'Shield Equipped') {
    return character.shield === true
  }

  if (reason === 'Second-Weapon Equipped') {
    return character.second_weapon === true
  }

  return true
}

function hasSwitchAdj(adjs, reason) {
  return adjs.some((adj) => adj?.name === 'cr' && `${adj?.adj_reason || ''}`.trim() === reason)
}

function createSyntheticSwitchAdj(reason) {
  return createAdjDetailEntry({
    id: null,
    name: 'cr',
    adj_reason: reason,
    adj_value: 1,
    character_item: null,
    editable: false,
    rune: '',
    source: 'ability',
    persistable: false,
  })
}

function ensureDerivedSwitchAbilityAdjs(character, abilityAdjs) {
  const normalizedAdjs = Array.isArray(abilityAdjs) ? [...abilityAdjs] : []

  if (character.shield === true && !hasSwitchAdj(normalizedAdjs, 'Shield Equipped')) {
    normalizedAdjs.push(createSyntheticSwitchAdj('Shield Equipped'))
  }

  if (character.second_weapon === true && !hasSwitchAdj(normalizedAdjs, 'Second-Weapon Equipped')) {
    normalizedAdjs.push(createSyntheticSwitchAdj('Second-Weapon Equipped'))
  }

  return normalizedAdjs
}
//-------------------
// load ability adjs
//-------------------
export default function loadAbilityAdjs(character) {
  console.log('-----------------------')
  console.log('IN loadAbilityAdjs')
  //-------------------------------------------------------------
  // NB: processes adjs values AND reasons (ability + rune adjs)
  //------------------------------------------------------------------------------------------
  // REPLACE DOWNLOADED character.ability_adjs to populate with createAdjDetailEntry entries
  //------------------------------------------------------------------------------------------
  abilityKeys.forEach((abilityKey) => {
    character[`${abilityKey}_adjs_tot`] = 0
    character[`${abilityKey}_adjs_reasons`] = ''
  })

  let ability_adjs = ensureDerivedSwitchAbilityAdjs(
    character,
    window.$deepCopy(character.ability_adjs || []),
  )
    .filter((adj) => isActiveSwitchAbilityAdj(character, adj))
    .filter((adj) => adj?.source !== 'integrated_rune')
  character.ability_adjs = []
  console.log('ABILITY ADJS TO PROCESS = ', window.$deepCopy(ability_adjs))
  //======================================================================================
  // A. loop adjs & aggregate reasons/values (non-rune ability adjs)
  //-----------------------------------------
  ability_adjs.forEach((a) => {
    let this_reason = ''
    //------------------------------------
    // total adjs values for this ability
    //------------------------------------
    character[`${a.name}_adjs_tot`] = (character[`${a.name}_adjs_tot`] ?? 0) + (a.adj_value || 0)
    //--------------------------------------
    // where reason exists for this ability
    //--------------------------------------
    if (a.adj_reason != '') {
      //-------------------------
      // build this reason string
      //--------------------------
      let reason = getAdjReason(a.adj_reason, a.adj_value)
      character = appendUniqueReason(character, a.name + '_adjs_reasons', reason)
    }
    //----------------------------------
    // prepare ability adjustment entry
    //----------------------------------
    let editable = true
    //-----------------------
    // determine if editable
    //-----------------------
    if (
      a.editable === false ||
      isReadonlyFatigueAdj(a.adj_reason) ||
      isReadonlySwitchAbilityAdj(character, a)
    ) {
      editable = false
    }
    //----------------------------------------
    // set reason for this ability adj entry
    //---------------------------------------
    if (!isEmpty(a.adj_reason)) {
      this_reason = a.adj_reason.charAt(0).toUpperCase() + a.adj_reason.slice(1)
    }
    //----------------------------------------------
    // create adjustment entry for this ability adj
    //----------------------------------------------
    character.ability_adjs.push(
      createAdjDetailEntry({
        id: a.id,
        name: a.name,
        adj_reason: this_reason,
        adj_value: a.adj_value,
        character_item: a.character_item,
        editable: editable,
        rune: '',
        source: 'ability',
        persistable: a.persistable === true || isPersistableSwitchAbilityAdj(a),
      }),
    )
  })
  //======================================================================================
  // B. loop any integrated runes + update value/reasons where ability name matches
  //--------------------------------------------------------------------------------
  character.runes.forEach((r) => {
    //-------------------------------------
    // example:
    // rune.name -> 'Disorder Rune' (rune used for reason description only)
    // ability_adjs -> name = 'sr'
    //-------------------------------------
    // loop any ability adjs for this rune
    //-------------------------------------
    r.ability_adjs.forEach((b) => {
      let this_reason = ''
      //------------------------------------
      // total adjs values for this ability
      //------------------------------------
      character[`${b.name}_adjs_tot`] = (character[`${b.name}_adjs_tot`] ?? 0) + (b.adj_value || 0)
      //--------------------------------------
      // where reason exists for this ability
      //--------------------------------------
      if (b.adj_reason != '') {
        //-------------------------
        // build this reason string
        //--------------------------
        let reason = getAdjReason(r.name, b.adj_value)
        character = appendUniqueReason(character, b.name + '_adjs_reasons', reason)
      }
      //----------------------------------------------------
      // set rune name as reason for this ability adj entry
      //----------------------------------------------------
      if (!isEmpty(r.name)) {
        this_reason = r.name.charAt(0).toUpperCase() + r.name.slice(1)
      }
      //-----------------------------------------------------
      // create adjustment entry for this (rune) ability adj
      //-----------------------------------------------------
      let entry = createAdjDetailEntry({
        id: b.id,
        name: b.name,
        adj_reason: this_reason,
        adj_value: b.adj_value,
        // use the rune's linked character_item (id) so
        // ability adjs behave like other integrated_rune
        // adjs and can resolve back to the item
        character_item: r.character_item ?? null,
        editable: false,
        rune: r.name,
        source: 'integrated_rune',
      })
      character.ability_adjs.push(entry)
    })
  })
  return character
}
